import React, { Suspense, lazy, useState, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, CircularProgress } from '@mui/material';
import { AuthProvider } from './contexts/AuthContext';
import { PermissionsProvider } from './contexts/PermissionsContext';
import ProtectedRoute from './components/ProtectedRoute';
import RequirePermission from './components/permissions/RequirePermission';
import { PERMS } from './config/pmPermissions';
import RequireAuth from './components/RequireAuth';
import OrgProtectedRoute from './components/OrgProtectedRoute';
import AppRedirect from './components/AppRedirect';
import MainLayout from './layouts/MainLayout';
import HubSelectionPage from './pages/HubSelectionPage';
import AddTransactionPage from './pages/app/AddTransactionPage';
import EditTransactionPage from './pages/app/EditTransactionPage';

const LandingPage = lazy(() => import('./pages/LandingPage'));
const ListingsPage = lazy(() => import('./pages/ListingsPage'));
const GalleryPage = lazy(() => import('./pages/GalleryPage'));
const ProjectDetailsPage = lazy(() => import('./pages/app/ProjectDetailsPage'));
const ProjectBudgetComparePage = lazy(() => import('./pages/app/ProjectBudgetComparePage'));
const ProjectsPage = lazy(() => import('./pages/app/ProjectsPage'));
const TransactionsPage = lazy(() => import('./pages/app/TransactionsPage'));
const InvoicesPage = lazy(() => import('./pages/app/InvoicesPage'));
const InvoiceFormPage = lazy(() => import('./pages/app/InvoiceFormPage'));
const InvoiceDetailsPage = lazy(() => import('./pages/app/InvoiceDetailsPage'));
const PayInvoicePage = lazy(() => import('./pages/app/PayInvoicePage'));
const ProductsPage = lazy(() => import('./pages/app/ProductsPage'));
const ProductFormPage = lazy(() => import('./pages/app/ProductFormPage'));
const ProductDetailsPage = lazy(() => import('./pages/app/ProductDetailsPage'));
const TransactionDetailsPage = lazy(() => import('./pages/app/TransactionDetailsPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const SignupPage = lazy(() => import('./pages/SignupPage'));
const QuoteRequestPage = lazy(() => import('./pages/QuoteRequestPage'));
const QuickBooksPage = lazy(() => import('./pages/quickbooks/QuickBooksPage'));
const SuppliersPage = lazy(() => import('./pages/app/SuppliersPage'));
const FundingSourcesPage = lazy(() => import('./pages/app/FundingSourcesPage'));
const ActivitiesPage = lazy(() => import('./pages/app/ActivitiesPage'));
const ProjectTemplatesPage = lazy(() => import('./pages/app/ProjectTemplatesPage'));
const DataFieldsPage = lazy(() => import('./pages/app/DataFieldsPage'));
const SectionsPage = lazy(() => import('./pages/app/SectionsPage'));
const SupplierDetailsPage = lazy(() => import('./pages/app/SupplierDetailsPage'));
const CompanyOverviewPage = lazy(() => import('./pages/app/CompanyOverviewPage'));
const AccountSettingsPage = lazy(() => import('./pages/app/AccountSettingsPage'));
const UserProfilePage = lazy(() => import('./pages/app/UserProfilePage'));
const QuickBooksIntegrationsSettingsPage = lazy(() => import('./pages/app/QuickBooksIntegrationsSettingsPage'));
const ExplorePage = lazy(() => import('./pages/app/ExplorePage'));
const QuickBooksReconciliationPage = lazy(() => import('./pages/app/QuickBooksReconciliationPage'));
const LicensePage = lazy(() => import('./pages/terms/LicensePage'));
const PrivacyPolicyPage = lazy(() => import('./pages/terms/PrivacyPolicyPage'));
const VendorHubPage = lazy(() => import('./pages/vendor/VendorHubPage'));
const VendorHubHome = lazy(() => import('./pages/vendor/VendorHubHome').then((mod) => ({ default: mod.VendorHubHome })));
const VendorQuoteDetailPage = lazy(() => import('./pages/vendor/VendorQuoteDetailPage'));
const VendorLinkedCompaniesPage = lazy(() => import('./pages/vendor/VendorLinkedCompaniesPage'));
const VendorBusinessProfilePage = lazy(() => import('./pages/vendor/VendorBusinessProfilePage'));
const CustomerLayout = lazy(() => import('./layouts/CustomerLayout'));
const CustomerProjectsPage = lazy(() => import('./pages/customer/CustomerProjectsPage'));
const CustomerProjectPage = lazy(() => import('./pages/customer/CustomerProjectPage'));
const CustomerProjectTransactionsPage = lazy(() => import('./pages/customer/CustomerProjectTransactionsPage'));
const CustomerProjectInvoicesPage = lazy(() => import('./pages/customer/CustomerProjectInvoicesPage'));
const CustomerProjectDetailsPage = lazy(() => import('./pages/customer/CustomerProjectDetailsPage'));

function PageLoader() {
    return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
            <CircularProgress />
        </Box>
    );
}

function App() {
    const [darkMode, setDarkMode] = useState(() => {
        const saved = localStorage.getItem('darkMode');
        return saved ? JSON.parse(saved) : false;
    });

    const theme = useMemo(() => createTheme({
        palette: {
            mode: darkMode ? 'dark' : 'light',
            primary: {
                main: '#1976d2',
            },
            secondary: {
                main: '#dc004e',
            },
        },
    }), [darkMode]);

    const toggleDarkMode = () => {
        const newMode = !darkMode;
        setDarkMode(newMode);
        localStorage.setItem('darkMode', JSON.stringify(newMode));
    };

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <AuthProvider>
                <PermissionsProvider>
                <Router>
                    <Suspense fallback={<PageLoader />}>
                        <Routes>
                            {/* Landing page at root */}
                            <Route path="/" element={<LandingPage />} />

                            {/* Public pages */}
                            <Route path="/listings" element={<ListingsPage />} />
                            <Route path="/gallery" element={<GalleryPage />} />

                            {/* Login / signup */}
                            <Route path="/login" element={<LoginPage />} />
                            <Route path="/signup" element={<SignupPage darkMode={darkMode} />} />

                            {/* Public quote request page - accessible without authentication */}
                            <Route path="/quotes/request/:id" element={<QuoteRequestPage darkMode={darkMode} toggleDarkMode={toggleDarkMode} />} />

                            {/* Public legal pages */}
                            <Route path="/terms/license" element={<LicensePage />} />
                            <Route path="/terms/privacy" element={<PrivacyPolicyPage />} />

                            <Route path="/hubs" element={
                                <RequireAuth>
                                    <HubSelectionPage darkMode={darkMode} />
                                </RequireAuth>
                            } />

                            <Route path="/vendor/:orgId/*" element={
                                <OrgProtectedRoute hubPrefix="vendor">
                                    <VendorHubPage darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
                                </OrgProtectedRoute>
                            }>
                                <Route index element={
                                    <RequirePermission permission={PERMS.VENDOR_QUOTES_VIEW}>
                                        <VendorHubHome />
                                    </RequirePermission>
                                } />
                                <Route path="quotes/:projectId" element={
                                    <RequirePermission permission={PERMS.VENDOR_QUOTES_VIEW}>
                                        <VendorQuoteDetailPage />
                                    </RequirePermission>
                                } />
                                <Route path="companies" element={
                                    <RequirePermission permission={PERMS.VENDOR_QUOTES_VIEW}>
                                        <VendorLinkedCompaniesPage />
                                    </RequirePermission>
                                } />
                                <Route path="business" element={
                                    <RequirePermission permission={PERMS.VENDOR_QUOTES_VIEW}>
                                        <VendorBusinessProfilePage />
                                    </RequirePermission>
                                } />
                                <Route path="profile" element={<UserProfilePage />} />
                            </Route>

                            <Route path="/customer/:orgId/*" element={
                                <OrgProtectedRoute hubPrefix="customer">
                                    <CustomerLayout />
                                </OrgProtectedRoute>
                            }>
                                <Route index element={
                                    <RequirePermission permission={PERMS.PROJECTS_VIEW}>
                                        <CustomerProjectsPage />
                                    </RequirePermission>
                                } />
                                <Route path="projects/:id" element={
                                    <RequirePermission permission={PERMS.PROJECTS_VIEW}>
                                        <CustomerProjectPage />
                                    </RequirePermission>
                                } />
                                <Route path="projects/:id/transactions" element={
                                    <RequirePermission anyOf={[PERMS.TRANSACTIONS_VIEW, PERMS.TRANSACTIONS_VIEW_LIMITED]}>
                                        <CustomerProjectTransactionsPage />
                                    </RequirePermission>
                                } />
                                <Route path="projects/:id/invoices" element={
                                    <RequirePermission anyOf={[PERMS.INVOICES_VIEW_LIMITED, PERMS.INVOICES_VIEW]}>
                                        <CustomerProjectInvoicesPage />
                                    </RequirePermission>
                                } />
                                <Route path="projects/:id/details" element={
                                    <RequirePermission anyOf={[PERMS.PROJ_DATAFIELDS_VIEW, PERMS.PROJ_DATAFIELDS_PUBLIC_VIEW]}>
                                        <CustomerProjectDetailsPage />
                                    </RequirePermission>
                                } />
                                <Route path="profile" element={<UserProfilePage />} />
                            </Route>

                            {/* All app routes under /app/:orgId - Protected (must come before /app/*) */}
                            <Route path="/app/:orgId/*" element={
                                <ProtectedRoute>
                                    <MainLayout darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
                                </ProtectedRoute>
                            }>
                                <Route path="overview" element={
                                    <RequirePermission permission={PERMS.ORG_ANALYTICS_VIEW}>
                                        <CompanyOverviewPage />
                                    </RequirePermission>
                                } />
                                <Route path="explore" element={
                                    <RequirePermission permission={PERMS.ORG_ANALYTICS_VIEW}>
                                        <ExplorePage />
                                    </RequirePermission>
                                } />
                                <Route path="profile" element={<UserProfilePage />} />
                                <Route path="settings" element={
                                    <RequirePermission permission={PERMS.ORGANIZATIONS_EDIT}>
                                        <AccountSettingsPage />
                                    </RequirePermission>
                                } />
                                <Route path="settings/quickbooks" element={
                                    <RequirePermission permission={PERMS.ORGANIZATIONS_EDIT}>
                                        <QuickBooksIntegrationsSettingsPage />
                                    </RequirePermission>
                                } />
                                <Route path="quickbooks/*" element={
                                    <RequirePermission permission={PERMS.TRANSACTIONS_RECONCILE}>
                                        <QuickBooksPage />
                                    </RequirePermission>
                                } />

                                {/* Projects */}
                                <Route path="projects" element={
                                    <RequirePermission permission={PERMS.PROJECTS_VIEW}>
                                        <ProjectsPage />
                                    </RequirePermission>
                                } />
                                <Route path="projects/:id/budgets/compare" element={
                                    <RequirePermission permission={PERMS.PROJ_BUDGETS_VIEW}>
                                        <ProjectBudgetComparePage />
                                    </RequirePermission>
                                } />
                                <Route path="projects/:id" element={
                                    <RequirePermission permission={PERMS.PROJECTS_VIEW}>
                                        <ProjectDetailsPage />
                                    </RequirePermission>
                                } />

                                {/* Transactions */}
                                <Route path="transactions" element={
                                    <RequirePermission permission={PERMS.TRANSACTIONS_VIEW}>
                                        <TransactionsPage />
                                    </RequirePermission>
                                } />
                                <Route path="transactions/quickbooks/reconcile" element={
                                    <RequirePermission permission={PERMS.TRANSACTIONS_RECONCILE}>
                                        <QuickBooksReconciliationPage />
                                    </RequirePermission>
                                } />
                                <Route path="transactions/add" element={
                                    <RequirePermission permission={PERMS.TRANSACTIONS_CREATE}>
                                        <AddTransactionPage />
                                    </RequirePermission>
                                } />
                                <Route path="transactions/:id/edit" element={
                                    <RequirePermission permission={PERMS.TRANSACTIONS_EDIT}>
                                        <EditTransactionPage />
                                    </RequirePermission>
                                } />
                                <Route path="transactions/:id" element={
                                    <RequirePermission permission={PERMS.TRANSACTIONS_VIEW}>
                                        <TransactionDetailsPage />
                                    </RequirePermission>
                                } />

                                <Route path="invoices" element={
                                    <RequirePermission permission={PERMS.INVOICES_VIEW}>
                                        <InvoicesPage />
                                    </RequirePermission>
                                } />
                                <Route path="invoices/new" element={
                                    <RequirePermission permission={PERMS.INVOICES_CREATE}>
                                        <InvoiceFormPage />
                                    </RequirePermission>
                                } />
                                <Route path="invoices/pay" element={
                                    <RequirePermission permission={PERMS.TRANSACTIONS_CREATE}>
                                        <PayInvoicePage />
                                    </RequirePermission>
                                } />
                                <Route path="invoices/:invoiceId/edit" element={
                                    <RequirePermission permission={PERMS.INVOICES_EDIT}>
                                        <InvoiceFormPage />
                                    </RequirePermission>
                                } />
                                <Route path="invoices/:invoiceId" element={
                                    <RequirePermission permission={PERMS.INVOICES_VIEW}>
                                        <InvoiceDetailsPage />
                                    </RequirePermission>
                                } />

                                <Route path="products/new" element={
                                    <RequirePermission permission={PERMS.INVOICES_CREATE}>
                                        <ProductFormPage />
                                    </RequirePermission>
                                } />
                                <Route path="products/:productId/edit" element={
                                    <RequirePermission permission={PERMS.INVOICES_EDIT}>
                                        <ProductFormPage />
                                    </RequirePermission>
                                } />
                                <Route path="products/:productId" element={
                                    <RequirePermission permission={PERMS.INVOICES_VIEW}>
                                        <ProductDetailsPage />
                                    </RequirePermission>
                                } />
                                <Route path="products" element={
                                    <RequirePermission permission={PERMS.INVOICES_VIEW}>
                                        <ProductsPage />
                                    </RequirePermission>
                                } />

                                {/* Suppliers */}
                                <Route path="suppliers/:id" element={
                                    <RequirePermission permission={PERMS.SUPPLIERS_VIEW}>
                                        <SupplierDetailsPage />
                                    </RequirePermission>
                                } />
                                <Route path="suppliers" element={
                                    <RequirePermission permission={PERMS.SUPPLIERS_VIEW}>
                                        <SuppliersPage />
                                    </RequirePermission>
                                } />

                                {/* Data / metadata */}
                                <Route path="accounts" element={
                                    <RequirePermission permission={PERMS.ORGANIZATIONS_VIEW}>
                                        <FundingSourcesPage />
                                    </RequirePermission>
                                } />
                                <Route path="activities" element={
                                    <RequirePermission permission={PERMS.PROJECTS_VIEW}>
                                        <ActivitiesPage />
                                    </RequirePermission>
                                } />
                                <Route path="project-templates" element={
                                    <RequirePermission permission={PERMS.ORGANIZATIONS_VIEW}>
                                        <ProjectTemplatesPage />
                                    </RequirePermission>
                                } />
                                <Route path="data-fields" element={
                                    <RequirePermission anyOf={[PERMS.ORGANIZATIONS_VIEW, PERMS.PROJ_DATAFIELDS_VIEW]}>
                                        <DataFieldsPage />
                                    </RequirePermission>
                                } />
                                <Route path="sections" element={
                                    <RequirePermission anyOf={[PERMS.ORGANIZATIONS_VIEW, PERMS.PROJ_DATAFIELDS_VIEW]}>
                                        <SectionsPage />
                                    </RequirePermission>
                                } />

                                {/* Default app route */}
                                <Route index element={
                                    <RequirePermission permission={PERMS.PROJECTS_VIEW}>
                                        <ProjectsPage />
                                    </RequirePermission>
                                } />
                            </Route>

                            {/* /app route and /app/* routes without orgId - redirects to /app/{orgId} or /app/{orgId}/* */}
                            <Route path="/app/*" element={<AppRedirect />} />
                        </Routes>
                    </Suspense>
                </Router>
                </PermissionsProvider>
            </AuthProvider>
        </ThemeProvider>
    );
}

export default App;
